import { message } from 'antd';
import { useCallback } from 'react';
import { deleteAdminProductCascade } from '../../services';
import type { FetchAdminProductsOptions, UseAdminProductCatalogArgs } from '../interfaces';
import { getApiErrorMessage } from '../../utils/apiError';

type UseAdminProductSearchActionsArgs = Pick<
  UseAdminProductCatalogArgs,
  | 'activeSearchTerm'
  | 'loadingMoreProducts'
  | 'loadingProducts'
  | 'searchTerm'
  | 'setActiveSearchTerm'
  | 'setBaseProducts'
  | 'setDeletingProductId'
  | 'setProducts'
  | 'setProductsHasMore'
  | 'setSearchTerm'
  | 'setSearchingProducts'
> & {
  fetchProducts: (options?: FetchAdminProductsOptions) => Promise<void>;
};

export const useAdminProductSearchActions = ({
  activeSearchTerm,
  fetchProducts,
  loadingMoreProducts,
  loadingProducts,
  searchTerm,
  setActiveSearchTerm,
  setBaseProducts,
  setDeletingProductId,
  setProducts,
  setProductsHasMore,
  setSearchTerm,
  setSearchingProducts,
}: UseAdminProductSearchActionsArgs) => {
  const handleDeleteProduct = useCallback(async (productId: string) => {
    try {
      setDeletingProductId(productId);
      await deleteAdminProductCascade(productId);
      await fetchProducts({ append: false, page: 1, search: activeSearchTerm });
      message.success('Product deleted with related data.');
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to delete product.'));
    } finally {
      setDeletingProductId(null);
    }
  }, [activeSearchTerm, fetchProducts, setDeletingProductId]);

  const handleProductPageChange = useCallback((page: number) => {
    if (loadingProducts || loadingMoreProducts) {
      return;
    }

    void fetchProducts({ append: false, page, search: activeSearchTerm });
  }, [activeSearchTerm, fetchProducts, loadingMoreProducts, loadingProducts]);

  const handleSearchProducts = useCallback(async () => {
    const normalizedSearch = searchTerm.trim();

    try {
      setSearchingProducts(true);
      setActiveSearchTerm(normalizedSearch);
      await fetchProducts({ append: false, page: 1, search: normalizedSearch });
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to search products.'));
    } finally {
      setSearchingProducts(false);
    }
  }, [fetchProducts, searchTerm, setActiveSearchTerm, setSearchingProducts]);

  const handleClearSearch = useCallback(async () => {
    setSearchTerm('');
    setActiveSearchTerm('');
    try {
      setSearchingProducts(true);
      await fetchProducts({ append: false, page: 1, search: '' });
    } finally {
      setSearchingProducts(false);
    }
  }, [fetchProducts, setActiveSearchTerm, setSearchTerm, setSearchingProducts]);

  return {
    handleClearSearch,
    handleDeleteProduct,
    handleProductPageChange,
    handleSearchProducts,
  };
};
